import round from 'lodash/round';

import {
  DataPopper,
  DataPopperRow,
  RatioProgressBar,
  RatioProgressBarProps,
  TooltipProps,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { DataTooltip } from '@cast/components-data-tooltip';

type Props = {
  usage: number;
  requests: number;
  wasted: number;
  label?: string;
  unit: string;
  warning?: boolean;
  ratioBarProps?: Partial<RatioProgressBarProps>;
  showValues?: boolean;
  tooltipProps?: Partial<TooltipProps>;
  sortValue?: number;
};

export const HourlyConsumedResources = ({
  requests,
  usage,
  wasted,
  label,
  unit,
  ratioBarProps,
  tooltipProps,
  sortValue,
  warning = false,
  showValues = true,
}: Props) => {
  return (
    <Stack gap={1} data-value={sortValue}>
      {showValues && (
        <Stack flexDirection="row" justifyContent="space-between">
          <Typography variant="inherit" data-testid="wasted" noWrap>
            {round(wasted, 6) || '-'}
          </Typography>
          <Typography
            variant="inherit"
            color={tokens.theme.fg.structural.secondary}
          >
            {unit}h
          </Typography>
        </Stack>
      )}
      <DataTooltip
        title={
          <DataPopper size="small" sx={{ minWidth: 210 }}>
            {label && (
              <DataPopperRow
                dividerBottom
                left={
                  <Typography
                    variant="inherit"
                    color={tokens.theme.fg.structural.secondary}
                  >
                    {label}
                  </Typography>
                }
              />
            )}
            <DataPopperRow
              left={
                <Typography variant="inherit" textTransform="none">
                  Used
                </Typography>
              }
              indicator={{ color: 'green.300' }}
              right={
                <Stack flexDirection="row" gap={4}>
                  <Typography variant="inherit" textTransform="none">
                    {round(usage, 3)}
                  </Typography>
                  <Typography
                    variant="inherit"
                    textTransform="none"
                    color={tokens.theme.fg.structural.secondary}
                  >
                    {unit}h
                  </Typography>
                </Stack>
              }
            />
            <DataPopperRow
              sx={{ opacity: requests ? 1 : 0.5 }}
              left={
                <Typography variant="inherit" textTransform="none">
                  Requested
                </Typography>
              }
              indicator={{
                color: 'blue.200',
                sx: {
                  border: warning ? 'yellow.500' : undefined,
                },
              }}
              right={
                requests ? (
                  <Stack flexDirection="row" gap={4}>
                    <Typography variant="inherit" textTransform="none">
                      {round(requests, 3)}
                    </Typography>
                    <Typography
                      variant="inherit"
                      textTransform="none"
                      color={tokens.theme.fg.structural.secondary}
                    >
                      {unit}h
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="inherit" color="yellow.500">
                    Not set
                  </Typography>
                )
              }
            />
            <DataPopperRow
              dividerTop
              left={
                <Typography variant="inherit" fontWeight={500}>
                  Wasted
                </Typography>
              }
              right={
                requests ? (
                  <Stack flexDirection="row" gap={4}>
                    <Typography variant="inherit" textTransform="none">
                      {round(wasted, 6)}
                    </Typography>
                    <Typography
                      variant="inherit"
                      textTransform="none"
                      color={tokens.theme.fg.structural.secondary}
                    >
                      {unit}h
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="inherit">-</Typography>
                )
              }
            />
          </DataPopper>
        }
        placement="top"
        {...tooltipProps}
      >
        <Box flexGrow={1} maxWidth="100%" minHeight={8}>
          <RatioProgressBar
            size="md"
            hideBorders
            sx={{ backgroundColor: 'yellow.500' }}
            bars={
              requests
                ? [
                    {
                      value: usage,
                      color: 'green.200',
                    },
                    {
                      value: requests,
                      color: 'blue.200',
                    },
                  ]
                : []
            }
            {...ratioBarProps}
          />
        </Box>
      </DataTooltip>
    </Stack>
  );
};
